import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, 
  Plus, 
  Trash2, 
  Loader2,
  Users,
  Settings,
  Mail,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default role keys and names
const DEFAULT_ROLE_NAMES = {
  fuehrungskraft: "Führungskraft",
  geschaeftsfuehrung: "Geschäftsführung",
  personalabteilung: "Personalabteilung",
  betriebsarzt: "Betriebsarzt",
  operator: "Operator"
};

// Default form labels
const DEFAULT_FORM_LABELS = {
  ersteller_name: "Name des Erstellers",
  behandlung: "Behandlung?",
  wegeunfall: "Wegeunfall?",
  arbeit_eingestellt: "Arbeit eingestellt?",
  geschaeftsfuehrung_informieren: "Soll die Geschäftsführung informiert werden?",
  verunfallter_name: "Name des Verunfallten",
  zeugen: "Zeugen",
  beschreibung: "Beschreibung des Unfallhergangs",
  fehltage: "Fehltage",
  koerperteil: "Verletztes Körperteil",
  verletzungsart: "Art der Verletzung",
  ursache: "Unfallursache",
  meldungsart: "Meldungsart",
  abteilung: "Abteilung / Ort",
  datum: "Unfalldatum"
};

// All available Meldungsarten
const ALL_MELDUNGSARTEN = [
  "Arbeitsunfall",
  "Verbandbucheintrag",
  "Beinaheunfall",
  "Sachschäden mit Sicherheitsbezug"
];

// Default Körperteile
const DEFAULT_KOERPERTEILE = [
  "Kopf", "Auge", "Ohr", "Hand", "Arm", "Schulter",
  "Rücken", "Bein", "Fuß", "Rumpf", "Mehrere", "Sonstige"
];

// Default Verletzungsarten
const DEFAULT_VERLETZUNGSARTEN = [
  "Schnittverletzung", "Prellung", "Quetschung", "Verbrennung", "Verätzung",
  "Bruch", "Stauchung", "Zerrung", "Augenverletzung", "Stromschlag", "Sonstige"
];

// Default Ursachen
const DEFAULT_URSACHEN = [
  "Menschliches Verhalten",
  "Arbeitsmittel & Werkzeuge",
  "Technische Mängel",
  "Organisatorische Mängel",
  "Arbeitsplatz oder Umgebungsbedingungen",
  "Persönliche Schutzausrüstung (PSA)",
  "Witterungseinflüsse",
  "Dritteinwirkung"
];

export default function CompanyManagement({ token }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [editAbteilungen, setEditAbteilungen] = useState("");
  const [editEmailPersonal, setEditEmailPersonal] = useState("");
  const [editEmailBetriebsarzt, setEditEmailBetriebsarzt] = useState("");
  const [editEmailGF, setEditEmailGF] = useState("");
  const [editRoleNames, setEditRoleNames] = useState({...DEFAULT_ROLE_NAMES});
  const [editFormLabels, setEditFormLabels] = useState({...DEFAULT_FORM_LABELS});
  const [editMeldungsarten, setEditMeldungsarten] = useState([...ALL_MELDUNGSARTEN]);
  const [editKoerperteile, setEditKoerperteile] = useState("");
  const [editVerletzungsarten, setEditVerletzungsarten] = useState("");
  const [editUrsachen, setEditUrsachen] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      toast.error("Fehler beim Laden der Unternehmen");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    
    if (!newCompanyName.trim()) {
      toast.error("Bitte Unternehmensnamen eingeben");
      return;
    }
    
    setCreating(true);
    
    try {
      await axios.post(`${API}/companies`, { name: newCompanyName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Unternehmen erstellt");
      setDialogOpen(false);
      setNewCompanyName("");
      fetchCompanies();
    } catch (error) {
      console.error("Error creating company:", error);
      toast.error("Fehler beim Erstellen des Unternehmens");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    try {
      await axios.delete(`${API}/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${companyName} wurde gelöscht`);
      fetchCompanies();
    } catch (error) {
      console.error("Error deleting company:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Fehler beim Löschen des Unternehmens");
      }
    }
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setEditAbteilungen((company.abteilungen || []).join("\n"));
    setEditEmailPersonal(company.email_personalabteilung || "");
    setEditEmailBetriebsarzt(company.email_betriebsarzt || "");
    setEditEmailGF(company.email_geschaeftsfuehrung || "");
    setEditRoleNames(company.custom_role_names || {...DEFAULT_ROLE_NAMES});
    setEditFormLabels(company.custom_form_labels || {...DEFAULT_FORM_LABELS});
    setEditMeldungsarten(company.enabled_meldungsarten || [...ALL_MELDUNGSARTEN]);
    setEditKoerperteile((company.custom_koerperteile || DEFAULT_KOERPERTEILE).join("\n"));
    setEditVerletzungsarten((company.custom_verletzungsarten || DEFAULT_VERLETZUNGSARTEN).join("\n"));
    setEditUrsachen((company.custom_ursachen || DEFAULT_URSACHEN).join("\n"));
    setEditDialogOpen(true);
  };

  const handleSaveAbteilungen = async () => {
    if (!selectedCompany) return;
    
    setSaving(true);
    
    try {
      const abteilungenArray = editAbteilungen
        .split("\n")
        .map(a => a.trim())
        .filter(a => a.length > 0);
      
      const koerperteileArray = editKoerperteile
        .split("\n")
        .map(a => a.trim())
        .filter(a => a.length > 0);
      
      const verletzungsartenArray = editVerletzungsarten
        .split("\n")
        .map(a => a.trim())
        .filter(a => a.length > 0);
      
      const ursachenArray = editUrsachen
        .split("\n")
        .map(a => a.trim())
        .filter(a => a.length > 0);
      
      await axios.put(`${API}/companies/${selectedCompany.id}`, 
        { 
          abteilungen: abteilungenArray,
          email_personalabteilung: editEmailPersonal || null,
          email_betriebsarzt: editEmailBetriebsarzt || null,
          email_geschaeftsfuehrung: editEmailGF || null,
          custom_role_names: editRoleNames,
          custom_form_labels: editFormLabels,
          enabled_meldungsarten: editMeldungsarten,
          custom_koerperteile: koerperteileArray.length > 0 ? koerperteileArray : null,
          custom_verletzungsarten: verletzungsartenArray.length > 0 ? verletzungsartenArray : null,
          custom_ursachen: ursachenArray.length > 0 ? ursachenArray : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Einstellungen gespeichert");
      setEditDialogOpen(false);
      fetchCompanies();
    } catch (error) {
      console.error("Error saving company settings:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleNameChange = (key, value) => {
    setEditRoleNames(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleFormLabelChange = (key, value) => {
    setEditFormLabels(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleMeldungsartToggle = (meldungsart) => {
    setEditMeldungsarten(prev => {
      if (prev.includes(meldungsart)) {
        // Don't allow removing the last one
        if (prev.length <= 1) return prev;
        return prev.filter(m => m !== meldungsart);
      } else {
        return [...prev, meldungsart];
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="company-management-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-500 mb-2">ADMINISTRATION</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Unternehmen
          </h1>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="create-company-button"
              className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neues Unternehmen
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">Neues Unternehmen erstellen</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Fügen Sie ein neues Unternehmen hinzu, das das Unfallmelde Tool nutzen kann.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCompany} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                  Unternehmensname
                </Label>
                <Input
                  id="name"
                  data-testid="new-company-name"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="z.B. Müller GmbH"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  disabled={creating}
                  data-testid="submit-new-company"
                  className="bg-yellow-500 text-black hover:bg-yellow-400 font-semibold"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Erstellen...
                    </>
                  ) : (
                    "Erstellen"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Box */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
        <p className="text-yellow-500 text-sm">
          <strong>Hinweis:</strong> Erstellen Sie hier Unternehmen für Ihre Kunden. 
          Jedes Unternehmen sieht nur seine eigenen Unfallmeldungen.
        </p>
      </div>

      {/* Companies List */}
      <div className="grid gap-4">
        {companies.map((company) => (
          <div 
            key={company.id}
            data-testid={`company-${company.id}`}
            className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4"
          >
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-sm flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="min-w-0 break-words">
                  <span className="font-medium text-zinc-50">{company.name}</span>
                  <p className="text-xs text-zinc-500">
                    Erstellt: {new Date(company.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <Button asChild variant="outline" className="border-zinc-700 text-yellow-500">
                  <Link data-testid={`company-email-chains-${company.id}`} to={`/unternehmen/${company.id}/email-ketten`}><Mail size={16} />E-Mail-Ketten</Link>
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => openEditDialog(company)}
                  className="text-zinc-500 hover:text-yellow-500 hover:bg-yellow-500/10"
                  title="Einstellungen"
                  data-testid={`company-settings-${company.id}`}
                >
                  <Settings className="w-4 h-4" />
                </Button>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      data-testid={`delete-company-${company.id}`}
                      className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-zinc-50">Unternehmen löschen?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    Möchten Sie {company.name} wirklich löschen? Alle zugehörigen Unfallmeldungen werden ebenfalls gelöscht.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    Abbrechen
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => handleDeleteCompany(company.id, company.name)}
                    className="bg-red-500 text-white hover:bg-red-600"
                  >
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
              </div>
            </div>
            
            {/* Show departments if set */}
            {company.abteilungen && company.abteilungen.length > 0 && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 mb-2">Abteilungen:</p>
                <div className="flex flex-wrap gap-1">
                  {company.abteilungen.map((abt, idx) => (
                    <span key={idx} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded">
                      {abt}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Show configured emails */}
            {(company.email_personalabteilung || company.email_betriebsarzt || company.email_geschaeftsfuehrung) && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 mb-2">E-Mail Benachrichtigungen:</p>
                <div className="space-y-1 text-xs">
                  {company.email_personalabteilung && (
                    <div className="text-zinc-400">
                      <span className="text-zinc-500">Personal:</span> {company.email_personalabteilung}
                    </div>
                  )}
                  {company.email_betriebsarzt && (
                    <div className="text-zinc-400">
                      <span className="text-zinc-500">Betriebsarzt:</span> {company.email_betriebsarzt}
                    </div>
                  )}
                  {company.email_geschaeftsfuehrung && (
                    <div className="text-zinc-400">
                      <span className="text-zinc-500">Geschäftsführung:</span> {company.email_geschaeftsfuehrung}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        
        {companies.length === 0 && (
          <div className="text-center py-12 border border-zinc-800 rounded-sm bg-zinc-900/50">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-500 mb-2">Noch keine Unternehmen angelegt</p>
            <p className="text-zinc-600 text-sm">Erstellen Sie ein Unternehmen, um Kunden hinzuzufügen</p>
          </div>
        )}
      </div>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-zinc-50">
              Einstellungen: {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              E-Mail-Adressen und Abteilungen für dieses Unternehmen verwalten.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* E-Mail Benachrichtigungen */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-4">
                E-Mail Benachrichtigungen
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Diese E-Mail-Adressen erhalten automatische Benachrichtigungen bei Unfallmeldungen 
                (je nach Meldungsart und Schweregrad).
              </p>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Personalabteilung
                  </Label>
                  <Input
                    type="email"
                    value={editEmailPersonal}
                    onChange={(e) => setEditEmailPersonal(e.target.value)}
                    placeholder="hr@firma.de"
                    data-testid="company-email-personal"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                  <p className="text-xs text-zinc-600">Erhält Benachrichtigungen bei allen Meldungen</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Betriebsarzt
                  </Label>
                  <Input
                    type="email"
                    value={editEmailBetriebsarzt}
                    onChange={(e) => setEditEmailBetriebsarzt(e.target.value)}
                    placeholder="betriebsarzt@firma.de"
                    data-testid="company-email-betriebsarzt"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                  <p className="text-xs text-zinc-600">Erhält Benachrichtigungen bei Arbeitsunfällen mit Ausfallzeit</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Geschäftsführung
                  </Label>
                  <Input
                    type="email"
                    value={editEmailGF}
                    onChange={(e) => setEditEmailGF(e.target.value)}
                    placeholder="geschaeftsfuehrung@firma.de"
                    data-testid="company-email-gf"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                  <p className="text-xs text-zinc-600">Erhält Benachrichtigungen nur bei schweren Unfällen (Checkbox)</p>
                </div>
              </div>
            </div>

            {/* Abteilungen */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-4">
                Abteilungen
              </h3>
              <Label className="text-zinc-400 text-sm">
                Abteilungen (eine pro Zeile)
              </Label>
              <Textarea
                value={editAbteilungen}
                onChange={(e) => setEditAbteilungen(e.target.value)}
                placeholder={"Verwaltung\nTechnik\nProduktion\n..."}
                className="bg-zinc-900 border-zinc-800 text-zinc-50 min-h-[150px] mt-2"
              />
              <p className="text-xs text-zinc-500 mt-2">
                Diese Abteilungen stehen bei Meldungen dieses Unternehmens zur Auswahl.
              </p>
            </div>

            {/* Custom Role Names */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-4">
                Rollennamen anpassen
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Passen Sie die Bezeichnungen der Rollen an die Begriffe Ihres Unternehmens an.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Führungskraft
                  </Label>
                  <Input
                    value={editRoleNames.fuehrungskraft || ""}
                    onChange={(e) => handleRoleNameChange("fuehrungskraft", e.target.value)}
                    placeholder="z.B. Teamleiter, Abteilungsleiter"
                    data-testid="role-name-fuehrungskraft"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Geschäftsführung
                  </Label>
                  <Input
                    value={editRoleNames.geschaeftsfuehrung || ""}
                    onChange={(e) => handleRoleNameChange("geschaeftsfuehrung", e.target.value)}
                    placeholder="z.B. Management, Direktion"
                    data-testid="role-name-geschaeftsfuehrung"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Personalabteilung
                  </Label>
                  <Input
                    value={editRoleNames.personalabteilung || ""}
                    onChange={(e) => handleRoleNameChange("personalabteilung", e.target.value)}
                    placeholder="z.B. HR, Personal"
                    data-testid="role-name-personalabteilung"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Betriebsarzt
                  </Label>
                  <Input
                    value={editRoleNames.betriebsarzt || ""}
                    onChange={(e) => handleRoleNameChange("betriebsarzt", e.target.value)}
                    placeholder="z.B. Werksarzt, Medical"
                    data-testid="role-name-betriebsarzt"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Operator
                  </Label>
                  <Input
                    value={editRoleNames.operator || ""}
                    onChange={(e) => handleRoleNameChange("operator", e.target.value)}
                    placeholder="z.B. Koordinator, Sachbearbeiter"
                    data-testid="role-name-operator"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
              </div>
            </div>

            {/* Meldungsarten aktivieren/deaktivieren */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-4">
                Meldungsarten
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Wählen Sie welche Meldungsarten für dieses Unternehmen verfügbar sein sollen.
              </p>
              
              <div className="space-y-3">
                {ALL_MELDUNGSARTEN.map(meldungsart => (
                  <div key={meldungsart} className="flex items-center gap-3">
                    <Switch
                      id={`meldungsart-${meldungsart}`}
                      checked={editMeldungsarten.includes(meldungsart)}
                      onCheckedChange={() => handleMeldungsartToggle(meldungsart)}
                      data-testid={`meldungsart-toggle-${meldungsart}`}
                    />
                    <Label 
                      htmlFor={`meldungsart-${meldungsart}`}
                      className="text-zinc-300 cursor-pointer"
                    >
                      {meldungsart}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Formular-Labels anpassen */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-4">
                Formular-Beschriftungen
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Passen Sie die Beschriftungen im Meldungsformular an.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Behandlung-Checkbox
                  </Label>
                  <Input
                    value={editFormLabels.behandlung || ""}
                    onChange={(e) => handleFormLabelChange("behandlung", e.target.value)}
                    placeholder="z.B. Arzt?, Behandlung?"
                    data-testid="form-label-behandlung"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Wegeunfall-Checkbox
                  </Label>
                  <Input
                    value={editFormLabels.wegeunfall || ""}
                    onChange={(e) => handleFormLabelChange("wegeunfall", e.target.value)}
                    placeholder="z.B. Wegeunfall?"
                    data-testid="form-label-wegeunfall"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Arbeit eingestellt-Checkbox
                  </Label>
                  <Input
                    value={editFormLabels.arbeit_eingestellt || ""}
                    onChange={(e) => handleFormLabelChange("arbeit_eingestellt", e.target.value)}
                    placeholder="z.B. Arbeit eingestellt?"
                    data-testid="form-label-arbeit-eingestellt"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50"
                  />
                </div>
              </div>
            </div>

            {/* Dropdown-Optionen anpassen */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-semibold text-yellow-500 uppercase tracking-wider mb-4">
                Dropdown-Optionen anpassen
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Passen Sie die Auswahlmöglichkeiten in den Dropdown-Menüs an (eine Option pro Zeile).
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Körperteile
                  </Label>
                  <Textarea
                    value={editKoerperteile}
                    onChange={(e) => setEditKoerperteile(e.target.value)}
                    placeholder="Eine Option pro Zeile"
                    data-testid="dropdown-koerperteile"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50 min-h-[150px] text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Verletzungsarten
                  </Label>
                  <Textarea
                    value={editVerletzungsarten}
                    onChange={(e) => setEditVerletzungsarten(e.target.value)}
                    placeholder="Eine Option pro Zeile"
                    data-testid="dropdown-verletzungsarten"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50 min-h-[150px] text-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">
                    Unfallursachen
                  </Label>
                  <Textarea
                    value={editUrsachen}
                    onChange={(e) => setEditUrsachen(e.target.value)}
                    placeholder="Eine Option pro Zeile"
                    data-testid="dropdown-ursachen"
                    className="bg-zinc-900 border-zinc-800 text-zinc-50 min-h-[150px] text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="border-zinc-700 text-zinc-300"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleSaveAbteilungen}
                disabled={saving}
                data-testid="save-company-settings"
                className="bg-yellow-500 text-black hover:bg-yellow-400"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  "Speichern"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
